import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "./ui/pagination";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import { ChartContainer, ChartTooltipContent } from "./ui/chart";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import fetchWithAuth from "../fetchWithAuth";

// Chart config for shadcn Area Chart - Axes style
const chartConfig = {
  planned: {
    label: "Запланировано кликов",
    color: "var(--color-planned)",
  },
  actual: {
    label: "Выполнено кликов",
    color: "var(--color-actual)",
  },
};

function formatDate(date) {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  const pad = n => n.toString().padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

// YYYY-MM-DD
function toYMD(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toISOString().slice(0, 10);
}

function isValidValue(val) {
  return typeof val === "number" && !isNaN(val);
}

export default function PlanFactTab({ projectId }) {
  const [stats, setStats] = useState([]);
  const [allStats, setAllStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [period, setPeriod] = useState("week"); // week, month, quarter
  const [sort, setSort] = useState({ field: "date", order: "desc" });
  const itemsPerPage = 30;

  // Загрузка данных
  const fetchData = useCallback((currentPage) => {
    if (!projectId) return;
    setLoading(true);

    // Таблица с пагинацией
    fetchWithAuth(`/api/project/${projectId}/repeat-stats?page=${currentPage}&pageSize=${itemsPerPage}`)
      .then(res => res.json())
      .then(response => {
        if (response && response.data && Array.isArray(response.data)) {
          let sorted = [...response.data];
          // Сортировка
          sorted.sort((a, b) => {
            if (sort.field === "date") {
              return sort.order === "asc"
                ? new Date(a.date) - new Date(b.date)
                : new Date(b.date) - new Date(a.date);
            }
            return 0;
          });
          setStats(sorted);
          setTotalPages(Math.ceil((response.total || 0) / itemsPerPage));
        } else {
          setStats([]);
          setTotalPages(1);
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Ошибка загрузки статистики");
        setLoading(false);
      });

    // Все данные для графика
    fetchWithAuth(`/api/project/${projectId}/repeat-stats?all=true`)
      .then(res => res.json())
      .then(response => {
        if (response && response.data && Array.isArray(response.data)) {
          const sorted = [...response.data].sort((a, b) => new Date(a.date) - new Date(b.date));
          setAllStats(sorted);
        } else {
          setAllStats([]);
        }
      })
      .catch(() => {});
  }, [projectId, itemsPerPage, sort]);

  useEffect(() => {
    fetchData(page);
  }, [projectId, page, fetchData]);

  // Текущая дата (YYYY-MM-DD)
  const todayYMD = toYMD(new Date());

  // Фильтрация данных для графика по периоду, без строк с сегодняшней датой и null
  const filteredChartData = useMemo(() => {
    if (!allStats.length) return [];
    const now = new Date();
    let startDate;
    switch (period) {
      case "week":
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        break;
      case "month":
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 1);
        break;
      case "quarter":
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 3);
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
    }
    return allStats.filter(stat => {
      const statYMD = toYMD(stat.date);
      return (
        statYMD !== todayYMD &&
        isValidValue(stat.plannedTasks) &&
        isValidValue(stat.actualTasks) &&
        new Date(stat.date) >= startDate
      );
    });
  }, [allStats, period, todayYMD]);

  // Подготовка данных для AreaChart
  const chartData = useMemo(() => {
    // Важно: значения должны быть числами, а не строками!
    return filteredChartData.map(stat => ({
      date: formatDate(stat.date),
      planned: Number(stat.plannedTasks),
      actual: Number(stat.actualTasks),
    }));
  }, [filteredChartData]);

  // Фильтрация данных для таблицы (без сегодняшней даты и null)
  const filteredStats = useMemo(() => {
    return stats.filter(stat => {
      const statYMD = toYMD(stat.date);
      return (
        statYMD !== todayYMD &&
        isValidValue(stat.plannedTasks) &&
        isValidValue(stat.actualTasks)
      );
    });
  }, [stats, todayYMD]);

  // Сортировка таблицы
  const handleSort = (field) => {
    setSort(prev => ({
      field,
      order: prev.order === "asc" ? "desc" : "asc",
    }));
    setPage(1);
  };

  // Цвета из tailwindcss (blue-300 и blue-950 в формате hsl)
  const chartVars = {
    "--color-planned": "hsl(207, 96%, 72%)", // blue-300
    "--color-actual": "hsl(222, 47%, 11%)",  // blue-950
  };

  return (
    <div className="space-y-6">
      {/* График */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle>План / Факт</CardTitle>
            <CardDescription>
              {period === "week" && "За последнюю неделю"}
              {period === "month" && "За последний месяц"}
              {period === "quarter" && "За последние 3 месяца"}
            </CardDescription>
          </div>
          <ToggleGroup
            type="single"
            value={period}
            onValueChange={value => value && setPeriod(value)}
            className="border rounded-md"
          >
            <ToggleGroupItem value="week">Неделя</ToggleGroupItem>
            <ToggleGroupItem value="month">Месяц</ToggleGroupItem>
            <ToggleGroupItem value="quarter">3 месяца</ToggleGroupItem>
          </ToggleGroup>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="flex justify-center items-center h-[350px]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}
          {!loading && chartData.length === 0 && (
            <div className="flex justify-center items-center h-[350px] text-gray-500">
              Нет данных для отображения графика
            </div>
          )}
          {!loading && chartData.length > 0 && (
            <ChartContainer config={chartConfig} style={{ minHeight: 350 }}>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart
                  data={chartData}
                  margin={{ left: -20, right: 12 }}
                >
                  <CartesianGrid vertical={false} stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tickCount={3}
                  />
                  <Tooltip cursor={false} content={<ChartTooltipContent />} />
                  <Area
                    dataKey="planned"
                    name="Запланировано кликов"
                    type="monotone"
                    fill="#d2e8ff"
                    fillOpacity={1}
                    stroke="#91c7ff"
                    strokeWidth={3}
                  />
                  <Area
                    dataKey="actual"
                    name="Выполнено кликов"
                    type="monotone"
                    fill="#aaccff"
                    fillOpacity={1}
                    stroke="#408dff"
                    strokeWidth={3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      {/* Таблица */}
      <CardHeader className="px-0 pt-0">
        <CardTitle>Количество запланированных и выполненных кликов</CardTitle>
      </CardHeader>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="w-[100px] cursor-pointer select-none"
                  onClick={() => handleSort("date")}
                >
                  Дата {sort.field === "date" ? (sort.order === "asc" ? "▲" : "▼") : ""}
                </TableHead>
                <TableHead className="w-[180px]">Запланировано кликов</TableHead>
                <TableHead className="w-[180px]">Выполнено кликов</TableHead>
                <TableHead className="w-[150px]">Выполнение, %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStats.map((stat, idx) => {
                const percent = stat.plannedTasks > 0
                  ? ((stat.actualTasks / stat.plannedTasks) * 100).toFixed(1) + '%'
                  : '—';
                return (
                  <TableRow key={stat.id || idx}>
                    <TableCell>{formatDate(stat.date)}</TableCell>
                    <TableCell>{stat.plannedTasks}</TableCell>
                    <TableCell>{stat.actualTasks}</TableCell>
                    <TableCell>{percent}</TableCell>
                  </TableRow>
                );
              })}
              {filteredStats.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-4 text-gray-500">
                    Нет данных
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Пагинация */}
      {!loading && filteredStats.length > 0 && (
        <div className="mt-4">
          <Pagination>
            <PaginationContent>
              {page > 1 && (
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      setPage(page - 1);
                    }}
                  />
                </PaginationItem>
              )}
              {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (page <= 3) {
                  pageNumber = i + 1;
                  if (i === 4) return (
                    <PaginationItem key={i}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                } else if (page >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                  if (i === 0) return (
                    <PaginationItem key={i}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                } else {
                  if (i === 0) return (
                    <PaginationItem key={i}>
                      <PaginationLink
                        href="#"
                        onClick={e => {
                          e.preventDefault();
                          setPage(1);
                        }}
                      >
                        1
                      </PaginationLink>
                    </PaginationItem>
                  );
                  if (i === 1) return (
                    <PaginationItem key={i}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                  if (i === 3) return (
                    <PaginationItem key={i}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                  if (i === 4) return (
                    <PaginationItem key={i}>
                      <PaginationLink
                        href="#"
                        onClick={e => {
                          e.preventDefault();
                          setPage(totalPages);
                        }}
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  );
                  pageNumber = page + i - 2;
                }
                return (
                  <PaginationItem key={i}>
                    <PaginationLink
                      href="#"
                      isActive={pageNumber === page}
                      onClick={e => {
                        e.preventDefault();
                        setPage(pageNumber);
                      }}
                    >
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              {page < totalPages && (
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      setPage(page + 1);
                    }}
                  />
                </PaginationItem>
              )}
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
